import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert } from '../components/ui/Alert';
import { buttonClass } from '../components/ui/Button';
import { Panel } from '../components/ui/Panel';
import { Skeleton } from '../components/ui/Skeleton';
import FadeIn from '../components/ui/FadeIn';
import {
  useWorkOrdersApi,
  workOrderErrorMessage,
  type Coverage,
  type CoverageLine,
  type WorkOrderStatus,
} from './workOrdersApi';

/** Past this many short parts the card names the first few and counts the rest. */
const LINES_SHOWN = 5;

const CheckIcon: React.FC = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="h-5 w-5 shrink-0 text-emerald-300">
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M10 1.5a8.5 8.5 0 1 1 0 17 8.5 8.5 0 0 1 0-17Zm3.9 5.4a.9.9 0 0 0-1.27.06L8.9 11.1 7.33 9.5a.9.9 0 1 0-1.28 1.27l2.24 2.24a.9.9 0 0 0 1.3-.03l4.37-4.82a.9.9 0 0 0-.06-1.27Z"
    />
  </svg>
);

const ShortIcon: React.FC = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="h-5 w-5 shrink-0 text-amber-300">
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M8.7 2.75a1.5 1.5 0 0 1 2.6 0l7 12.25A1.5 1.5 0 0 1 17 17.25H3A1.5 1.5 0 0 1 1.7 15l7-12.25ZM10 6.5a.9.9 0 0 0-.9.9v3.9a.9.9 0 0 0 1.8 0V7.4a.9.9 0 0 0-.9-.9Zm0 6.6a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z"
    />
  </svg>
);

const QuietIcon: React.FC = () => (
  <svg aria-hidden="true" viewBox="0 0 20 20" fill="none" className="h-5 w-5 shrink-0 text-on-surface-variant">
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M10 1.5a8.5 8.5 0 1 1 0 17 8.5 8.5 0 0 1 0-17Zm0 1.8a6.7 6.7 0 1 0 0 13.4 6.7 6.7 0 0 0 0-13.4ZM6 9.1h8a.9.9 0 0 1 0 1.8H6a.9.9 0 0 1 0-1.8Z"
    />
  </svg>
);

const plural = (n: number) => `${n} ${n === 1 ? 'part' : 'parts'}`;

const ShortLines: React.FC<{ lines: CoverageLine[] }> = ({ lines }) => {
  const shown = lines.slice(0, LINES_SHOWN);
  const rest = lines.length - shown.length;
  return (
    <ul aria-label="Short parts" className="mt-2 space-y-1 text-sm">
      {shown.map((l) => (
        <li key={l.key} className="flex min-w-0 items-baseline gap-3">
          <span className="min-w-0 flex-1 truncate text-on-surface" title={l.target_name}>
            {l.target_name}
          </span>
          <span className="whitespace-nowrap tabular-nums text-on-surface">Need {l.short} more</span>
          <span className="hidden whitespace-nowrap text-xs tabular-nums text-on-surface-variant sm:inline">
            {l.covered} of {l.needed} in Assembly
          </span>
        </li>
      ))}
      {rest > 0 && <li className="text-xs text-on-surface-variant">and {plural(rest)} more on the pick list</li>}
    </ul>
  );
};

function notCountedHeadline(status: WorkOrderStatus): string {
  if (status === 'on_hold') return 'On hold: not counted in Assembly';
  if (status === 'suggested') return 'Not approved yet: not counted in Assembly';
  return 'Not counted in Assembly';
}

interface Props {
  orderId: string;
  orderStatus: WorkOrderStatus;
  /** Changes whenever the run page reloads, so the card reads the pool again. */
  refreshKey: string;
}

/** Whether the one Assembly pool covers this run, claimed oldest run first. */
const CoverageCard: React.FC<Props> = ({ orderId, orderStatus, refreshKey }) => {
  const woApi = useWorkOrdersApi();
  const [coverage, setCoverage] = useState<Coverage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Picks happen on the pick list, often on another device: re-read on return and every 30s.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    const timer = window.setInterval(bump, 30_000);
    window.addEventListener('focus', bump);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', bump);
    };
  }, []);

  useEffect(() => {
    let live = true;
    setError(null);
    woApi
      .coverage(orderId)
      .then((c) => {
        if (live) setCoverage(c);
      })
      .catch((err) => {
        if (live) setError(workOrderErrorMessage(err, 'Coverage did not load.'));
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, refreshKey, attempt, tick]);

  const openPickList = (
    <Link to="/pick-list" className={`${buttonClass('quiet', 'md')} shrink-0`}>
      Open the pick list
    </Link>
  );

  let icon: React.ReactNode = <QuietIcon />;
  let headline = 'Parts';
  let body: React.ReactNode = null;

  if (!coverage && error) {
    body = (
      <Alert tone="error" variant="note" onRetry={() => setAttempt((n) => n + 1)}>
        {error}
      </Alert>
    );
  } else if (!coverage) {
    body = (
      <div aria-busy="true" aria-label="Checking Assembly">
        <Skeleton className="h-4 w-48 rounded" />
      </div>
    );
  } else {
    const short = coverage.lines.filter((l) => l.short > 0);
    switch (coverage.status) {
      case 'covered':
        icon = <CheckIcon />;
        headline = 'Parts: covered';
        body = <p className="m-0 text-sm text-on-surface-variant">Assembly holds what this run still needs.</p>;
        break;
      case 'short':
        icon = <ShortIcon />;
        headline = `Short on ${plural(short.length)}`;
        body = (
          <>
            <p className="m-0 text-sm text-on-surface-variant">
              Older runs draw from Assembly first. Pick these to cover this one.
            </p>
            <ShortLines lines={short} />
          </>
        );
        break;
      case 'no_bom':
        headline = 'No parts list';
        body = (
          <p className="m-0 text-sm text-on-surface-variant">
            This model has no current parts list, so the run asks Assembly for nothing yet.
          </p>
        );
        break;
      case 'not_counted':
        headline = notCountedHeadline(orderStatus);
        body = (
          <>
            <p className="m-0 text-sm text-on-surface-variant">
              {short.length
                ? `Counted behind every approved run, it would be short on ${plural(short.length)}.`
                : 'Counted behind every approved run, it would be covered.'}
            </p>
            {short.length > 0 && <ShortLines lines={short} />}
          </>
        );
        break;
      default:
        break;
    }
  }

  return (
    <Panel
      title={
        <span className="flex min-w-0 items-center gap-2">
          {icon}
          <span title={headline} className="truncate">
            {headline}
          </span>
        </span>
      }
      actions={openPickList}
    >
      <FadeIn key={!coverage ? (error ? 'error' : 'loading') : coverage.status}>{body}</FadeIn>
    </Panel>
  );
};

export default CoverageCard;
